import {
	useContext,
	useEffect,
	useState,
	startTransition,
	lazy,
	Suspense,
} from "react";
import ReactDOM from "react-dom";
import { urlParams, validateParam } from "utils/paramsValidation";
import { Context } from "Context";
import appConfig from "config.json";
import { localeRegExp } from "utils/language";
import Header from "components/header/Header";
import { canRender, canRenderGenesysChat } from "utils/canRenderValidation";
import { isDevelopmentEnvironment } from "utils";

// lazy loaded components
const TravelAgentHeader = lazy(() =>
	import("components/travel-agent-header/TravelAgentHeader")
);
const AriaLiveRegion = lazy(() => import("./components/shared/AriaLiveRegion"));
const Footer = lazy(() => import("components/footer/Footer"));
const Legal = lazy(() => import("components/legal/Legal"));
const Seo = lazy(() => import("components/shared/Seo"));
const FloatingHelpHub = lazy(() =>
	import("components/floating-help-hub/FloatingHelpHub")
);
const Modal = lazy(() => import("components/shared/Modal"));
const Survey = lazy(() => import("components/shared/Survey"));
const Chat = lazy(() => import("components/chat/Chat"));
const GenesysChat = lazy(() =>
	import("components/chat/genesys-chat/GenesysChat")
);
const NewChat = lazy(() => import("components/chat/new-chat/NewChat"));

const headerComponent = "header";
const messageCenterComponent = "message-center";
const travelAgentHeaderComponent = "travel-agent-header";
const legalComponent = "legal";
const footerComponent = "footer";

const App = () => {
	const {
		config: {
			header,
			footer,
			legal,
			brand,
			hideOnPrint,
			oneIdOnly,
			skipOneId,
			environment,
			locale,
		},
		chat: { chatEnvironment },
	} = useContext(Context);

	const [defer, setDefer] = useState(false);
	useEffect(() => {
		startTransition(() => setDefer(true));
	}, []);

	appConfig.skipOneId = skipOneId;

	const renderComponent = (targetContainer, component, customRender) => {
		let targetId = urlParams.get(`${targetContainer}Container`);

		if (!targetId && targetContainer === legalComponent) {
			targetId = urlParams.get(`${footerComponent}Container`);
		}

		const elementToRender = document?.getElementById(
			targetId || `syndicated-${targetContainer}`
		);

		if (!elementToRender) return null;

		if (targetContainer === headerComponent) {
			elementToRender?.classList?.add("syndicated-header-parent");
			elementToRender.setAttribute("role", "banner");
		}

		if (appConfig.brands[brand]?.modifyZIndex) {
			elementToRender?.classList?.add("syndicated-header-parent-zindex");
		}

		if (hideOnPrint) {
			elementToRender?.classList?.add("syndicated-no-print");
		}

		if (customRender) {
			customRender(elementToRender);
		} else {
			return ReactDOM.createPortal(component, elementToRender);
		}
	};

	const canRenderChat = () => {
		if (chatEnvironment) return false;
		if (appConfig.brands[brand]?.externalScripts?.lpTagMessaging) {
			if (
				appConfig.brands[
					brand
				].externalScripts.lpTagMessaging.excludedEnvironments?.includes(
					environment
				)
			) {
				return false;
			}
			return true;
		} else {
			return false;
		}
	};

	const canRenderNewChat = () => {
		const brandConfiguration = appConfig.brands[brand]?.newChatConfiguration;
		if (
			chatEnvironment &&
			brandConfiguration &&
			(isDevelopmentEnvironment() ||
				brandConfiguration.allowedDomains?.some((allowedDomain) =>
					window.location.origin.endsWith(allowedDomain)
				)) &&
			brandConfiguration?.allowedLocales?.includes(locale)
		) {
			return true;
		} else {
			return false;
		}
	};

	const renderChatComponent = () => {
		if (canRenderGenesysChat("genesys", appConfig, brand, environment, locale)) {
			return (
				<Suspense fallback={null}>
					<GenesysChat environment={environment} />
				</Suspense>
			);
		}
		if (canRenderNewChat()) {
			return (
				<Suspense fallback={null}>
					<NewChat />
				</Suspense>
			);
		}
		if (canRenderChat()) {
			return (
				<Suspense fallback={null}>
					<Chat />
				</Suspense>
			);
		}
		return null;
	};

	const renderMessageCenterComponent = () => {
		if (validateParam(messageCenterComponent)) {
			(async () => {
				const { renderMessageCenter } = await import("loaders/messageCenter");
				renderComponent(messageCenterComponent, null, renderMessageCenter);
			})();
		}
		return null;
	};

	useEffect(() => {
		const setLangAttribute = brand === "wdw" || brand === "dlr";
		if (setLangAttribute) {
			let lang = "en-us";
			const origin = window?.location.origin;
			if (origin.endsWith(".eu")) {
				lang = "en-eu";
			} else if (origin.endsWith(".co.uk")) {
				lang = "en-gb";
			} else {
				const currentLocaleFromPath =
					window?.location.pathname.match(localeRegExp)?.[0] || "";
				lang = currentLocaleFromPath
					? currentLocaleFromPath
							.toLowerCase()
							.replace(/\//g, "")
							.replace(/_/g, "-")
					: lang;
			}
			document?.getElementsByTagName("html")[0].setAttribute("lang", lang);
		}
	}, [brand]);

	return (
		<div id="syndicated-root__app">
			{oneIdOnly ? (
				<></>
			) : (
				<>
					<Suspense fallback={null}>
						<AriaLiveRegion />
					</Suspense>
					{header && renderComponent(headerComponent, <Header />)}
					{defer && renderChatComponent()}
					{defer && renderMessageCenterComponent()}
					{defer && validateParam(travelAgentHeaderComponent) && (
						<Suspense fallback={null}>
							{renderComponent(
								travelAgentHeaderComponent,
								<TravelAgentHeader isolated />
							)}
						</Suspense>
					)}
					{defer && footer && (
						<Suspense fallback={null}>
							{renderComponent(footerComponent, <Footer />)}
						</Suspense>
					)}
					{defer && legal && (
						<Suspense fallback={null}>
							{renderComponent(legalComponent, <Legal />)}
						</Suspense>
					)}
					{defer && (
						<Suspense fallback={null}>
							<Modal />
						</Suspense>
					)}
					{defer && (
						<Suspense fallback={null}>
							<Survey />
						</Suspense>
					)}
					{defer && canRender("seo", appConfig, brand, environment) && (
						<Suspense fallback={null}>
							<Seo />
						</Suspense>
					)}
					{defer &&
						canRender("floatingHelpHub", appConfig, brand, environment) && (
							<Suspense fallback={null}>
								<FloatingHelpHub />
							</Suspense>
						)}
				</>
			)}
		</div>
	);
};

export default App;
